import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './PaymentSuccessModal.css';

interface PaymentSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  donationData: {
    amount: number;
    project: string;
    donationFrequency: string;
    paymentMethod: string;
    paymentStatus: string;
    transactionId?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    createdAt?: string;
    donationId?: string;
  };
}

const PaymentSuccessModal: React.FC<PaymentSuccessModalProps> = ({
  isOpen,
  onClose,
  donationData
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const formatDate = (date?: string) => {
    if (!date) return new Date().toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const generateTransactionId = () => {
    return donationData.transactionId || `TXN${Date.now()}${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
  };

  const downloadReceipt = () => {
    const receiptContent = generateReceiptHTML();
    const blob = new Blob([receiptContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Donation_Receipt_${generateTransactionId()}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generateReceiptHTML = () => {
    const transactionId = generateTransactionId();
    const date = formatDate(donationData.createdAt);
    
    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Donation Receipt - ${transactionId}</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            max-width: 800px;
            margin: 0 auto;
            padding: 40px 20px;
            background-color: #f5f5f5;
        }
        .receipt-container {
            background: white;
            padding: 40px;
            border-radius: 12px;
            box-shadow: 0 4px 16px rgba(0,0,0,0.1);
        }
        .header {
            text-align: center;
            border-bottom: 3px solid #f4c430;
            padding-bottom: 20px;
            margin-bottom: 30px;
        }
        .header h1 {
            color: #1a1a1a;
            margin: 0;
            font-size: 28px;
        }
        .header p {
            color: #666;
            margin: 5px 0;
        }
        .success-badge {
            background: linear-gradient(135deg, #f4c430 0%, #e6a800 100%);
            color: white;
            padding: 12px 24px;
            border-radius: 8px;
            display: inline-block;
            font-weight: bold;
            margin: 20px 0;
        }
        .receipt-details {
            margin: 30px 0;
        }
        .detail-row {
            display: flex;
            justify-content: space-between;
            padding: 12px 0;
            border-bottom: 1px solid #eee;
        }
        .detail-label {
            font-weight: 600;
            color: #333;
        }
        .detail-value {
            color: #666;
            text-align: right;
        }
        .amount-highlight {
            background: #fff3cd;
            padding: 20px;
            border-radius: 8px;
            margin: 20px 0;
            text-align: center;
        }
        .amount-highlight .label {
            font-size: 14px;
            color: #666;
            margin-bottom: 5px;
        }
        .amount-highlight .value {
            font-size: 32px;
            font-weight: bold;
            color: #1a1a1a;
        }
        .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #eee;
            text-align: center;
            color: #666;
            font-size: 12px;
        }
        .transaction-id {
            background: #f8f9fa;
            padding: 15px;
            border-radius: 8px;
            margin: 20px 0;
            text-align: center;
            font-family: monospace;
            font-size: 14px;
            color: #333;
        }
        @media print {
            body {
                background: white;
                padding: 0;
            }
            .receipt-container {
                box-shadow: none;
            }
        }
    </style>
</head>
<body>
    <div class="receipt-container">
        <div class="header">
            <h1>Manjushree Foundation</h1>
            <p>Donation Receipt</p>
            <div class="success-badge">✓ Payment Successful</div>
        </div>
        
        <div class="transaction-id">
            Transaction ID: ${transactionId}
        </div>
        
        <div class="receipt-details">
            <div class="detail-row">
                <span class="detail-label">Date & Time:</span>
                <span class="detail-value">${date}</span>
            </div>
            ${donationData.firstName ? `
            <div class="detail-row">
                <span class="detail-label">Donor Name:</span>
                <span class="detail-value">${donationData.firstName} ${donationData.lastName || ''}</span>
            </div>
            ` : ''}
            ${donationData.email ? `
            <div class="detail-row">
                <span class="detail-label">Email:</span>
                <span class="detail-value">${donationData.email}</span>
            </div>
            ` : ''}
            ${donationData.phone ? `
            <div class="detail-row">
                <span class="detail-label">Phone:</span>
                <span class="detail-value">${donationData.phone}</span>
            </div>
            ` : ''}
            <div class="detail-row">
                <span class="detail-label">Project:</span>
                <span class="detail-value">${donationData.project}</span>
            </div>
            <div class="detail-row">
                <span class="detail-label">Donation Frequency:</span>
                <span class="detail-value">${donationData.donationFrequency === 'one-time' ? 'One-time' : 
                  donationData.donationFrequency === 'monthly' ? 'Monthly' :
                  donationData.donationFrequency === 'quarterly' ? 'Quarterly' : 'Yearly'}</span>
            </div>
            <div class="detail-row">
                <span class="detail-label">Payment Method:</span>
                <span class="detail-value">${donationData.paymentMethod === 'card' ? 'Credit/Debit Card' : 'UPI Payment'}</span>
            </div>
            <div class="detail-row">
                <span class="detail-label">Payment Status:</span>
                <span class="detail-value">${donationData.paymentStatus === 'success' ? 'Success' : donationData.paymentStatus}</span>
            </div>
        </div>
        
        <div class="amount-highlight">
            <div class="label">Donation Amount</div>
            <div class="value">₹${donationData.amount.toLocaleString('en-IN')}</div>
        </div>
        
        <div class="footer">
            <p><strong>Thank you for your generous contribution!</strong></p>
            <p>This receipt serves as proof of your donation to Manjushree Foundation.</p>
            <p>For any queries, please contact us at info@manjushreefoundation.org</p>
            <p style="margin-top: 20px;">Generated on ${new Date().toLocaleString('en-IN')}</p>
        </div>
    </div>
</body>
</html>
    `;
  };

  const transactionId = generateTransactionId();

  return (
    <div className="payment-success-modal-overlay" onClick={onClose}>
      <div className="payment-success-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>×</button>
        
        <div className="modal-header">
          <div className="success-icon">✓</div>
          <h2>{t('paymentSuccess.title', 'Payment Successful!')}</h2>
          <p className="success-message">
            {t('paymentSuccess.message', 'Thank you for your generous donation!')}
          </p>
        </div>

        <div className="modal-content">
          <div className="transaction-info">
            <div className="transaction-id">
              <span className="label">{t('paymentSuccess.transactionId', 'Transaction ID')}:</span>
              <span className="value">{transactionId}</span>
            </div>
          </div>

          <div className="payment-details">
            <h3>{t('paymentSuccess.paymentDetails', 'Payment Details')}</h3>
            <div className="detail-row">
              <span className="detail-label">{t('paymentSuccess.amount', 'Amount')}:</span>
              <span className="detail-value">₹{donationData.amount.toLocaleString('en-IN')}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">{t('paymentSuccess.project', 'Project')}:</span>
              <span className="detail-value">{donationData.project}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">{t('paymentSuccess.frequency', 'Frequency')}:</span>
              <span className="detail-value">
                {donationData.donationFrequency === 'one-time' ? t('donationForm.frequencies.oneTime', 'One-time') :
                 donationData.donationFrequency === 'monthly' ? t('donationForm.frequencies.monthly', 'Monthly') :
                 donationData.donationFrequency === 'quarterly' ? t('donationForm.frequencies.quarterly', 'Quarterly') :
                 t('donationForm.frequencies.yearly', 'Yearly')}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">{t('paymentSuccess.paymentMethod', 'Payment Method')}:</span>
              <span className="detail-value">
                {donationData.paymentMethod === 'card' ? t('donationForm.paymentMethods.card', 'Credit/Debit Card') : 
                 t('donationForm.paymentMethods.upi', 'UPI Payment')}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">{t('paymentSuccess.date', 'Date')}:</span>
              <span className="detail-value">{formatDate(donationData.createdAt)}</span>
            </div>
            {donationData.firstName && (
              <div className="detail-row">
                <span className="detail-label">{t('paymentSuccess.donor', 'Donor')}:</span>
                <span className="detail-value">{donationData.firstName} {donationData.lastName || ''}</span>
              </div>
            )}
          </div>

          <div className="dashboard-notice">
            <div className="notice-icon">📊</div>
            <div className="notice-content">
              <h4>{t('paymentSuccess.dashboardTitle', 'View in Donator Dashboard')}</h4>
              <p>{t('paymentSuccess.dashboardMessage', 'You can view all your donation history, download receipts, and manage recurring donations from your Donator Dashboard.')}</p>
              <button 
                className="btn-view-dashboard"
                onClick={() => {
                  navigate('/dashboard');
                  onClose();
                }}
              >
                {t('paymentSuccess.goToDashboard', 'Go to Dashboard')}
              </button>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn-download-receipt" onClick={downloadReceipt}>
            📄 {t('paymentSuccess.downloadReceipt', 'Download Receipt')}
          </button>
          <button className="btn-close-modal" onClick={onClose}>
            {t('paymentSuccess.close', 'Close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessModal;


